import { useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { ALLERGENS } from "../data/allergens";

// AllergiesSection — manage the user's SAFETY-CRITICAL allergen list
// (profiles.allergies). Tap-list of the Big-9 + free-text customs. Active
// allergens read in a warning/rose tone (these are EXCLUSIONS, not picks),
// distinct from the gold "diet preference" chips above. Photo/document
// scan-to-extract is a sibling CTA wired in a later phase (onScanPhoto).
//
// Stored shape: jsonb array of allergen ids ("milk","tree_nut") + custom
// strings ("kiwi"). Writes go straight through upsertProfile so the user's
// own change appears instantly (useProfile writes its cache on upsert).

// Two modes:
//   * Uncontrolled (Settings): pass profile + upsertProfile — writes
//     go straight to the DB on every toggle.
//   * Controlled (onboarding / AllergySheetReview): pass value +
//     onChange — chips edit LOCAL state and nothing touches the
//     network until the caller decides (onboarding writes once at
//     completion; the sheet-review writes on explicit SAVE).
export default function AllergiesSection({
  profile, upsertProfile, onScanPhoto = null,
  value, onChange, hideHeader = false,
}) {
  const controlled = typeof onChange === "function";
  const stored = controlled
    ? (Array.isArray(value) ? value : [])
    : (Array.isArray(profile?.allergies) ? profile.allergies : []);
  const [saving, setSaving] = useState(false);
  const [custom, setCustom] = useState("");

  const has = (id) => stored.includes(id);
  const knownIds = new Set(ALLERGENS.map((a) => a.id));
  const customs = stored.filter((x) => typeof x === "string" && !knownIds.has(x));

  const write = async (next) => {
    if (controlled) { onChange(next); return; }
    if (saving) return;
    setSaving(true);
    try { await upsertProfile?.({ allergies: next }); }
    finally { setSaving(false); }
  };

  const toggle = (id) => write(has(id) ? stored.filter((x) => x !== id) : [...stored, id]);
  const addCustom = () => {
    const v = custom.trim().toLowerCase();
    if (!v) return;
    if (!stored.some((x) => String(x).toLowerCase() === v)) write([...stored, v]);
    setCustom("");
  };
  const removeCustom = (val) => write(stored.filter((x) => x !== val));

  return (
    <>
      {!hideHeader && (
        <div style={{
          fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, letterSpacing: "0.14em",
          color: COLOR.dim, margin: "22px 0 10px",
        }}>
          ALLERGIES &amp; SAFETY
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
        {ALLERGENS.map((a) => {
          const active = has(a.id);
          return (
            <button
              key={a.id}
              onClick={() => toggle(a.id)}
              disabled={saving}
              className="mcm-focusable"
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
                fontFamily: FONT.forma, fontSize: 13, fontWeight: active ? 700 : 500,
                color: active ? "#fff" : COLOR.ink,
                background: active ? COLOR.rose : COLOR.soil,
                border: `1px solid ${active ? COLOR.rose : COLOR.border}`,
                borderRadius: 10, cursor: saving ? "wait" : "pointer", textAlign: "left",
                touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>{a.emoji}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{a.label}</span>
              {active && <span style={{ fontSize: 13, fontWeight: 700 }}>✕</span>}
            </button>
          );
        })}
      </div>

      {/* Custom allergens */}
      {customs.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {customs.map((c) => (
            <button
              key={c}
              onClick={() => removeCustom(c)}
              disabled={saving}
              className="mcm-focusable"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 11px",
                fontFamily: FONT.forma, fontSize: 13, fontWeight: 600, color: "#fff",
                background: COLOR.rose, border: "none", borderRadius: 999,
                cursor: saving ? "wait" : "pointer", touchAction: "manipulation",
              }}
            >
              <span style={{ textTransform: "capitalize" }}>{c}</span>
              <span style={{ fontSize: 12 }}>✕</span>
            </button>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addCustom(); }}
          placeholder="Add another (e.g. mustard, kiwi)"
          style={{
            flex: 1, padding: "10px 12px", fontFamily: FONT.forma, fontSize: 13,
            color: COLOR.ink, background: COLOR.soil, border: `1px solid ${COLOR.border}`,
            borderRadius: 10, outline: "none", minWidth: 0,
          }}
        />
        <button
          onClick={addCustom}
          disabled={saving || !custom.trim()}
          className="mcm-focusable"
          style={{
            padding: "10px 16px", fontFamily: FONT.forma, fontSize: 13, fontWeight: 700,
            color: custom.trim() ? COLOR.night : COLOR.dim,
            background: custom.trim() ? COLOR.gold : COLOR.soil,
            border: `1px solid ${custom.trim() ? COLOR.gold : COLOR.border}`,
            borderRadius: 10, cursor: custom.trim() ? "pointer" : "default",
            touchAction: "manipulation",
          }}
        >
          ADD
        </button>
      </div>

      {onScanPhoto && (
        <button
          onClick={onScanPhoto}
          className="mcm-focusable"
          style={{
            marginTop: 8, width: "100%", padding: "12px", fontFamily: FONT.forma,
            fontSize: 13, fontWeight: 600, color: COLOR.ink, background: "transparent",
            border: `1px dashed ${COLOR.border}`, borderRadius: 10, cursor: "pointer",
            touchAction: "manipulation",
          }}
        >
          Scan a photo of your allergy list
        </button>
      )}

      <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.dim, marginTop: 8, lineHeight: 1.5 }}>
        Allergens are <strong>never</strong> generated into recipes, and any
        kitchen item or recipe that contains one gets flagged. Safety-critical —
        kept separate from diet preferences.
      </div>
    </>
  );
}
